/**
 * Datova vrstva detailu zakazky nad API (krok S3).
 *
 * Nahrazuje puvodni fixture vrstvu, ktera cetla commitnuty JSON z `public/`.
 * JEDINY modul, ktery vi, odkud data detailu prichazeji — dnes ze tri
 * REALNYCH endpointu:
 *
 *   1. `GET /v1/deals/:zakazkaId/audit-runs`      — ktery auditni pripad
 *      (`dd_cases.id`) pod zakazkou visi + kdy beh zacal/skoncil,
 *   2. `GET /v1/duediligence/cases/:ddId/detail`  — pripad, fakta, otazky,
 *      dukazy, brany, dalsi nejlepsi kroky,
 *   3. `GET /v1/mcp/deal/:ddId`                   — rozhodovaci pripravenost
 *      (povinne brany X/Y) a cenove pasmo z Trust vrstvy.
 *
 * Tvar, ktery vraci, je `CaseDetail` z `@reos/reports` — stejny kontrakt,
 * jaky mela fixture, takze vsechny selektory a zalozky detailu zustavaji beze
 * zmeny.
 *
 * Krok O5a zavrel mezery z REPORT-step-S3.md §4: `/detail` uz vraci trust band
 * na fakt (`claims`), situacni plan (`sitePlan`), uzly a relace grafu,
 * checklist a duvod zastaveni otazky; podklady na vstupu prisly ctvrtym
 * endpointem `GET …/cases/:ddId/inputs`. Co API porad neposila (archivni
 * snapshot dukazu, skore NBA), se sem NEDOMYSLI — pole zustavaji prazdna
 * a UI to poctive rekne.
 */

import type { R3CaseFixture } from '@reos/duediligence';
import { fixtureFromApi, toCaseDetail, type ApiCaseDetailLike, type CaseDetail } from '@reos/reports';
import type { ApiPaths } from '@reos/api-client';
import { apiClient } from './api.js';
import { unwrap } from './api-error.js';
import type { CasePhaseKey } from './texts.js';

export type { CaseDetail };

type DdCaseDetailResponse =
  ApiPaths['/v1/duediligence/cases/{id}/detail']['get']['responses']['200']['content']['application/json'];
type DealGetResponse = ApiPaths['/v1/mcp/deal/{id}']['get']['responses']['200']['content']['application/json'];
type DealTimelineResponse =
  ApiPaths['/v1/mcp/deal/{id}/timeline']['get']['responses']['200']['content']['application/json'];
type AuditRunListResponse =
  ApiPaths['/v1/deals/{dealId}/audit-runs']['get']['responses']['200']['content']['application/json'];
type InputListResponse =
  ApiPaths['/v1/duediligence/cases/{id}/inputs']['get']['responses']['200']['content']['application/json'];
type AuditRunDto = AuditRunListResponse['items'][number];

/**
 * Zakazka jeste nema auditni pripad (nikdy nebyl spusteny audit). Neni to
 * chyba prenosu — detail ma ukazat stavovou hlasku, ne cervenou chybu.
 */
export class NoAuditCaseError extends Error {
  constructor() {
    super('dd-api: zakazka nema auditni pripad');
    this.name = 'NoAuditCaseError';
  }
}

// ---- mapovani API -> R3CaseFixture -----------------------------------------

/**
 * Mapovani samo zije v `@reos/reports` (`fixtureFromApi`, krok O5b) — server
 * generuje exporty z TEHOZ tvaru, takze dokument a obrazovka nesmi mit kazdy
 * svoji verzi prevodu. Tady zbyva jen slozeni ctyr odpovedi dohromady.
 */
function toFixture(
  detail: DdCaseDetailResponse,
  deal: DealGetResponse,
  timeline: DealTimelineResponse['items'],
  run: AuditRunDto | null,
  inputs: InputListResponse['items'],
): R3CaseFixture {
  return fixtureFromApi({
    // `openapi-typescript` prepisuje zod tuple [number, number] na number[] a
    // enum bran na sirsi string — tvar je stejny (server odpoved validuje
    // schematem), zuzuje se jen typ.
    detail: detail as unknown as ApiCaseDetailLike,
    priceBand: deal.priceBand,
    decisionReadiness: deal.decisionReadiness,
    timeline,
    inputs,
    run,
  });
}

// ---- verejne API vrstvy -----------------------------------------------------

/**
 * Auditni pripad (`dd_cases.id`) pod zakazkou + posledni beh. Vazba vede pres
 * `audit_runs` (`deal_id` = zakazka, `case_id` = auditni pripad) — jina cesta
 * ze zakazky na auditni pripad dnes v API neni (Nalezy S3).
 */
export async function resolveAuditCase(
  zakazkaId: string,
): Promise<{ readonly ddCaseId: string; readonly run: AuditRunDto }> {
  const result = await unwrap(
    apiClient.GET('/v1/deals/{dealId}/audit-runs', {
      params: { path: { dealId: zakazkaId }, query: { limit: 20 } },
    }),
  );
  const run = result.items[0];
  if (run === undefined) throw new NoAuditCaseError();
  return { ddCaseId: run.caseId, run };
}

/**
 * Krok QAFIX14: detail vraci i BEH, ze ktereho je slozeny. Obrazovka z nej
 * pozna ukazkovy (prehrany) beh demo kancelare a beh, ktery jeste bezi —
 * oboji musi platit i po refreshi, kdy zadna odpoved na „Spustit prověření"
 * neexistuje. Druhy dotaz na tentyz beh by to nestalo za to (R5/O6a).
 */
export interface CaseDetailResult {
  readonly detail: CaseDetail;
  readonly run: AuditRunDto;
}

/**
 * Jedina vstupni funkce datove vrstvy detailu. `zakazkaId` je `cases.id`
 * (zakazka), ne auditni pripad — obrazovka pracuje s tim, co je v URL.
 */
export async function loadCaseDetail(zakazkaId: string): Promise<CaseDetailResult> {
  const { ddCaseId, run } = await resolveAuditCase(zakazkaId);
  const [detail, deal, timeline, inputs] = await Promise.all([
    unwrap(apiClient.GET('/v1/duediligence/cases/{id}/detail', { params: { path: { id: ddCaseId } } })),
    unwrap(apiClient.GET('/v1/mcp/deal/{id}', { params: { path: { id: ddCaseId } } })),
    unwrap(apiClient.GET('/v1/mcp/deal/{id}/timeline', { params: { path: { id: ddCaseId } } })),
    unwrap(apiClient.GET('/v1/duediligence/cases/{id}/inputs', { params: { path: { id: ddCaseId } } })),
  ]);
  return { detail: toCaseDetail(toFixture(detail, deal, timeline.items, run, inputs.items)), run };
}

export type { AuditRunDto };

/**
 * Faze zakazky odvozena z prubehu behu auditu (MODEL-ZAKAZKY-ROZHODNUTI.md) —
 * prevzato beze zmeny z puvodni fixture vrstvy.
 */
export function casePhase(detail: CaseDetail): CasePhaseKey {
  const { research } = detail.fixture;
  if (research.startedAt === null) return 'KONCEPT';
  return research.finishedAt !== null ? 'AUDIT_DOKONCENO' : 'AUDIT_PROBIHA';
}

export type {
  AccessSummary,
  AreaReconciliation,
  AreaRow,
  CaseHeaderData,
  DomainConfidenceRow,
  FactImpactSeverity,
  GateVerdict,
  HistoryEvent,
  R3CaseFixture,
  R3FixtureEvidence,
  R3FixtureFact,
  RiskSummary,
  UtilitiesSummary,
  ValuationScenario,
} from '@reos/reports';

export {
  accessSummary,
  areaReconciliationResult,
  areaRows,
  caseHeader,
  conflictFacts,
  domainConfidencePct,
  domainConfidenceRows,
  evidenceRows,
  factImpactSeverity,
  historyEvents,
  factCaption,
  narrowingFactorsSorted,
  openQuestionsSorted,
  riskSummary,
  tabFacts,
  toCaseDetail,
  utilitiesSummary,
  valuationScenarios,
} from '@reos/reports';
